import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { useSessionLifecycle } from './useSessionLifecycle';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AppNavigation } from './AppNavigation';
import { StatusProvider } from './StatusProvider';
import { APP_NAVIGATION_EVENT, handleAppLinkClick } from './navigationActions';
import { hasAppRoute, loadRoute } from './routeLoaders';

const initialPath = location.pathname;
// Start only the requested page's chunk while React mounts the navigation shell.
if (hasAppRoute(initialPath)) void loadRoute(initialPath).catch(() => {});

export function AppRouter() {
  const [route, setRoute] = useState(() => `${location.pathname}${location.search}`);
  const path = new URL(route, location.origin).pathname;
  const [loaded, setLoaded] = useState<{ path: string; Page: ComponentType } | null>(null);
  const [loadError, setLoadError] = useState('');
  const Page = loaded?.path === path ? loaded.Page : null;
  useEffect(() => {
    if (!hasAppRoute(path)) return;
    let cancelled = false;
    setLoadError('');
    void loadRoute(path).then((Component) => { if (!cancelled) setLoaded({ path, Page: Component }); })
      .catch(() => { if (!cancelled) setLoadError('页面加载失败，请刷新重试'); });
    return () => { cancelled = true; };
  }, [path]);
  const embeddedStructure = path === '/structure-v2' && new URLSearchParams(location.search).get('embedded') === '1' && window.parent !== window;
  useEffect(() => {
    const update = () => setRoute(`${location.pathname}${location.search}`);
    window.addEventListener('popstate', update);
    window.addEventListener(APP_NAVIGATION_EVENT, update);
    document.addEventListener('click', handleAppLinkClick);
    return () => { window.removeEventListener('popstate', update); window.removeEventListener(APP_NAVIGATION_EVENT, update); document.removeEventListener('click', handleAppLinkClick); };
  }, []);
  useSessionLifecycle(path);
  if (!hasAppRoute(path)) return <main className="grid min-h-screen place-items-center bg-[#080b11] text-slate-400"><p>找不到这个页面</p></main>;
  const page = <AppErrorBoundary key={route}>{Page ? <Page /> : <main role="status" className="grid min-h-[45vh] place-items-center bg-[#080b11] text-sm text-slate-500">{loadError || '正在载入页面…'}</main>}</AppErrorBoundary>;
  if (path === '/login') return <StatusProvider>{page}</StatusProvider>;
  return <StatusProvider><div className="min-h-screen bg-[#080b11] text-slate-100">{!embeddedStructure && <AppNavigation />}{page}</div></StatusProvider>;
}
